import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Navigate, useSearchParams} from 'react-router-dom';
import {useInfiniteQuery, useQuery, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {sizes} from 'patient-ping-remedy/packages/theme';
import Button from 'patient-ping-remedy/packages/button';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import {unionBy} from 'lodash-es';
import {differenceInCalendarDays, subDays} from 'date-fns';
import {FeatureToggle} from '@/api/dto/dto';
import {FilterResponse, FilterView} from '@/api/dto/filters';
import WorkSummary from "@/components/pages/tasks/WorkSummary";
import {
  CareJourneyResponse,
  formatTaskFiltersForApi,
  triggeringEventTypeDisplayValues,
} from '@/api/dto/care-journey';
import {SearchParam} from '@/helpers/search_params';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {useActiveRoster, useRosterStore} from '@/store/roster_store';
import SortableTable, {SORT_ORDER, SortableTableHandle, TableColumn} from '@common/SortableTable';
import AwsCommunicationsDrawer from '@/components/pages/tasks/aws_communications/AwsCommunicationsDrawer';
import Helpers from '@/helpers/helpers';
import {buildTaskFilterOptions} from '@/helpers/task_filter_options';
import {dateRange, FilterInvalidate} from "@common/FilterComponent";
import {
  MixpanelClientListEvent,
  MixpanelEvent,
  MixpanelEventType,
  MixpanelFilterEvent,
} from "@/api/dto/mixpanel";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import {useTaskStepName} from "@/hooks/useTaskStepName";
import {useMixpanel} from "@/hooks/useMixpanel";
import {useFilterQuery} from "@/hooks/useFilterQuery";
import {useSearchQuery} from "@/hooks/useSearchQuery";
import {SortOrderType} from "@clients/ClientsPage";
import {buildCareJourneyRows} from "@/helpers/care_journey_rows";
import {
  AttestationStatusOptions,
  DaysOpenOptions, DaysSinceDischargeOptions,
  EventSettingOptions,
  EventTypeOptions,
  PriorityOptions,
  StatusOptions, TaskTypeOptions,
  TasksSearchBy,
  searchByLabels,
  searchByDropdownLabels
} from "@/components/pages/tasks/TasksCommon";

const tableHeaders = [
  "Name",
  "Date of Birth",
  "Event type",
  "Event Setting",
  "Days Open",
  "Group / Org",
  "Task type",
  "Status",
  "Next Step",
  "Priority",
  "Last Viewed",
  "Assigned to"
];

export function formatLastViewed(lastViewedAt: string | null): string {
  if (!lastViewedAt) return '';
  const days = differenceInCalendarDays(new Date(), new Date(lastViewedAt));
  return days < 1 ? '<1 Day' : `${days} ${days === 1 ? 'Day ago' : 'Days ago'}`;
}


export default function CareJourneysTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const oldTaskId = searchParams.get(SearchParam.TASK_ID);

  const {carecoApi} = useContext(CarecoApiContext);
  const {currentUser} = useAuthStore();
  const [careJourneys, setCareJourneys] = useState<CareJourneyResponse[]>([]);
  const tableRef = useRef<SortableTableHandle>(null);
  const {taskStepDisplayValues, isLoadingTaskStepNames} = useTaskStepName();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const {checkBooleanToggle} = useFeatureToggles();
  const activeRoster = useActiveRoster();
  const selectedRoster = useRosterStore((state) => state.selectedRoster);


  const rosterTotalRef = useRef<number>(0);
  const searchTotalRef = useRef<number>(0);
  const sortKeyRef = useRef<string | undefined>('eventType');
  const sortOrderRef = useRef<SortOrderType | undefined>(SORT_ORDER.DESC);
  const queryClient = useQueryClient();

  const [currentSearchBy, setCurrentSearchBy] = useState(() => {
    return localStorage.getItem('search_by_tasks_page') || TasksSearchBy.NAME;
  });
  const [searchTermLength, setSearchTermLength] = useState(0);

  const awsCommunications = searchParams.get(SearchParam.AWS_COMMUNICATIONS);

  const {data: assignedToOptions} = useQuery({
    queryKey: ['assignedToOptions'],
    queryFn: () => carecoApi?.getCareJourneyAssignmentOptions(),
    enabled: !!carecoApi,
  });

  const {data: groupOptions} = useQuery({
    queryKey: ['groupOptions'],
    queryFn: () => carecoApi?.getTaskRosters(),
    enabled: !!carecoApi,
  });

  const {data: rosters} = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi,
  });

  const {data: careJourneysData, fetchNextPage, isLoading, error, isFetching} = useInfiniteQuery({
    queryKey: ['careJourneysData'],
    queryFn: ({pageParam}) => {
      const isTaskIdSearch = currentSearchBy === TasksSearchBy.TASK_ID;
      const suppressTaskIdSearch = searchTermLength === 4 && searchTerm.length < searchTermLength;
      const searchValue = queryFnSearch();
      return carecoApi?.getCareJourneys(pageParam, {
        sortBy: sortKeyRef.current,
        sortOrder: sortOrderRef.current?.value,
        ...formatTaskFiltersForApi(params),
        searchName: !isTaskIdSearch ? searchValue : null,
        searchTaskId: isTaskIdSearch && !suppressTaskIdSearch ? searchValue : null,
      });
    },
    enabled: !!carecoApi,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
  });

  const invalidate = useCallback<FilterInvalidate>(() => {
    void queryClient.removeQueries({queryKey: ['careJourneysData']});
    setCareJourneys([]);
  }, [queryClient]);

  const {params, queryFnCallback} = useFilterQuery({
    invalidate,
    params: {
      statuses: [StatusOptions[0].value],
      priorities: [],
      eventTypes: [],
      daysOpen: [],
      daysSinceDischarge: [],
      taskDate: {} as dateRange,
      taskTypes: [],
      dueDate: {} as dateRange,
      nextSteps: [],
      lastViewed: {} as dateRange,
      assignees: [],
      attestationStatuses: [],
      groups: [],
    },
    multipleRoutesRendered: false,
    invalidateOnUnMount: false,
  });

  const appliedFiltersFromCache = queryClient.getQueryData<{
    filters: Record<string, unknown>;
  }>(['appliedTaskFilters']);

  const filterMountedRef = useRef(false);
  useEffect(() => {
    if (!filterMountedRef.current) {
      filterMountedRef.current = true;
      return;
    }
    if (params) {
      const hasFilters = Object.values(params).some(val => {
        if (Array.isArray(val)) return val.length > 0;
        if (val && typeof val === 'object')
          return Object.keys(val as unknown as Record<string, unknown>).length > 0;
        return !!val;
      });
      if (hasFilters) {
        queryClient.setQueryData(['appliedTaskFilters'], { filters: params });
      } else {
        queryClient.setQueryData(['appliedTaskFilters'], null);
      }
    }
  }, [params, queryClient]);

  const getCachedFilters = () => {
    if (!appliedFiltersFromCache?.filters) {
      return undefined;
    }
    const cached = appliedFiltersFromCache.filters;
    const transformed: Record<string, unknown> = {};

    if (Array.isArray(cached.statuses) && cached.statuses.length > 0) {
      transformed.statuses = StatusOptions.filter(o =>
        (cached.statuses as string[]).includes(o.value));
    }
    if (Array.isArray(cached.priorities) && cached.priorities.length > 0) {
      transformed.priorities = PriorityOptions.filter(o =>
        (cached.priorities as string[]).includes(o.value));
    }
    if (Array.isArray(cached.eventTypes) && cached.eventTypes.length > 0) {
      transformed.eventTypes = EventTypeOptions.filter(o =>
        (cached.eventTypes as string[]).includes(o.value));
    }
    if (Array.isArray(cached.eventSetting) && cached.eventSetting.length > 0) {
      transformed.eventSetting = EventSettingOptions.filter(o =>
        (cached.eventSetting as string[]).includes(o.value));
    }
    if (Array.isArray(cached.daysOpen) && cached.daysOpen.length > 0) {
      transformed.daysOpen = DaysOpenOptions.filter(o =>
        (cached.daysOpen as string[]).includes(o.value));
    }
    if (Array.isArray(cached.taskTypes) && cached.taskTypes.length > 0) {
      transformed.taskTypes = TaskTypeOptions.filter(o =>
        (cached.taskTypes as string[]).includes(o.value));
    }
    if (Array.isArray(cached.lastCompletedSteps)
      && cached.lastCompletedSteps.length > 0) {
      transformed.lastCompletedSteps = Object.entries(taskStepDisplayValues)
        .filter(([value]) =>
          (cached.lastCompletedSteps as string[]).includes(value))
        .map(([value, label]) => ({id: value, value, label}));
    }
    if (Array.isArray(cached.assignees) && cached.assignees.length > 0) {
      transformed.assignees = (assignedToOptions ?? []).filter(o =>
        (cached.assignees as string[]).includes(o.value));
    }
    if (Array.isArray(cached.attestationStatuses) && cached.attestationStatuses.length > 0) {
      transformed.attestationStatuses = AttestationStatusOptions.filter(o =>
        (cached.attestationStatuses as string[]).includes(o.value));
    }
    if (cached.taskDate && typeof cached.taskDate === 'object'
      && Object.keys(cached.taskDate).length > 0) {
      transformed.taskDate = cached.taskDate;
    }
    if (cached.dueDate && typeof cached.dueDate === 'object'
      && Object.keys(cached.dueDate).length > 0) {
      transformed.dueDate = cached.dueDate;
    }
    if (Array.isArray(cached.groups) && cached.groups.length > 0) {
      transformed.groups = (groupOptions ?? []).filter(o =>
        (cached.groups as string[]).includes(o.value));
    }

    return Object.keys(transformed).length > 0 ? transformed : undefined;
  };

  const {searchTerm, searchFunction, queryFnSearch} = useSearchQuery({
    invalidate,
    queryFnCallback,
    canInvalidate,
  });

  function canInvalidate() {
    setSearchTermLength(searchTerm.length);
    return currentSearchBy === TasksSearchBy.NAME
      || (currentSearchBy === TasksSearchBy.TASK_ID
        && (searchTerm.length > 3 || searchTerm.length === 0))
      || (currentSearchBy === TasksSearchBy.TASK_ID
        && searchTermLength === 4 && searchTerm.length < searchTermLength);
  }

  const handleSearchByChange = (value: string) => {
    setCurrentSearchBy(value);
    localStorage.setItem('search_by_tasks_page', value);
  };

  useEffect(() => {
    if (careJourneysData) {
      const fetchedCareJourneys = careJourneysData.pages.flatMap(page => page?.data.careJourneys ?? []);
      rosterTotalRef.current = careJourneysData.pages[0]?.data.rosterTotal ?? 0;
      searchTotalRef.current = careJourneysData.pages[0]?.data.searchTotal ?? 0;
      setCareJourneys((prevCareJourneys : CareJourneyResponse[]) => prevCareJourneys ?
        unionBy(fetchedCareJourneys, prevCareJourneys, 'careJourneyInstanceId') : fetchedCareJourneys);
    }
  }, [careJourneysData]);

  const postMixpanelEvent = (response: FilterResponse) => {
    if (!response) return;

    const filterData = Helpers.safeJsonParse(response.data, {}) as Record<string, unknown>;
    const filterFieldsUsed = Helpers.expandFilterKeys(filterData as Record<string, unknown>);

    const event: MixpanelFilterEvent = {
      eventName: MixpanelEventType.BIH_PINNED_FILTER_APPLIED,
      filterId: response.id,
      filterName: response.name,
      filterOwnerId: response.ownerId,
      filterCriteriaCount: filterFieldsUsed.length,
      filterFieldsUsed,
      filterView: response.view,
      fieldsAdded: [],
      fieldsRemoved: [],
      ...mixpanelEventProperties!,
    };

    carecoApi?.postMixpanelEvent(event);
  };

  const handleNextPage = () => {
    if (careJourneysData?.pages.at(-1)?.data.careJourneys.length) {
      void fetchNextPage();
    }
  };

  const handleSortChange = (sortKey: TableColumn, sortOrder: SortOrderType) => {
    sortKeyRef.current = sortKey.key;
    sortOrderRef.current = sortOrder;
    invalidate();
  };

  const resetSort = () => {
    sortKeyRef.current = undefined;
    sortOrderRef.current = undefined;
  };

  const getEventType = useCallback((careJourney: CareJourneyResponse) => {
    return triggeringEventTypeDisplayValues[careJourney.triggeringEventType];
  }, []);

  const tableData = useMemo(() => buildCareJourneyRows(careJourneys, {
    getEventType,
    taskStepDisplayValues,
    findRoster: (rosterId) => rosters?.data?.data?.find((r) => r.id === rosterId),
  }), [careJourneys, getEventType, taskStepDisplayValues, rosters]);

  useEffect(() => {
    if (mixpanelEventProperties) {
      const event: MixpanelEvent = {
        eventName: MixpanelEventType.BIH_TASKS_PAGE_VIEWED,
        ...mixpanelEventProperties,
      };
      carecoApi?.postMixpanelEvent(event);
    }
  }, [mixpanelEventProperties]);

  // Redirect old task detail links (/tasks?t=<id>&r=<rosterId>) to new route
  if (oldTaskId) {
    return <Navigate to={`/tasks/details?${searchParams}`} replace />;
  }

  const closeAwsCommunicationsDrawer = () => {
    if (searchParams.has(SearchParam.AWS_COMMUNICATIONS)) {
      searchParams.delete(SearchParam.AWS_COMMUNICATIONS);
      setSearchParams(searchParams);
    }
  };

  return (
    <><>
      <WorkSummary
        onAssignedClick={(currentUserUuid) => {
          tableRef.current?.applyFilter({
            statuses: [StatusOptions[0]],
            assignees: [{label: currentUser?.first_name ?? '', value: currentUserUuid, id: currentUserUuid}],
          });
        }}
        onOverdueClick={(currentUserUuid) => {
          tableRef.current?.applyFilter({
            statuses: [StatusOptions[0]],
            assignees: [{label: currentUser?.first_name ?? '', value: currentUserUuid, id: currentUserUuid}],
            dueDate: {startDate: null, endDate: subDays(new Date(), 1)},
          });
        }}
        onPinnedFilterClick={(filter) => {
          tableRef.current?.applyFilter(filter);
          postMixpanelEvent(filter);
        }}/>
      <SortableTable
        id={FilterView.TASKS_TABLE}
        loading={isLoading || isLoadingTaskStepNames}
        fetching={isFetching}
        data={tableData}
        loadError={error}
        header={tableHeaders}
        sortableColumns={tableData[0] ? [
          tableData[0]['eventType'],
          tableData[0]['daysOpen'],
          tableData[0]['groupOrg'],
          tableData[0]['taskType'],
          tableData[0]['status'],
          tableData[0]['nextStep'],
          tableData[0]['priority'],
        ] : []}
        filterOptions={buildTaskFilterOptions({
          query: {params, invalidate},
          eventTypeOptions: EventTypeOptions,
          eventSettingOptions: EventSettingOptions,
          daysOpenOptions: DaysOpenOptions,
          daysSinceDischargeOptions: DaysSinceDischargeOptions,
          includeTaskDate: true,
          taskTypeOptions: TaskTypeOptions,
          statusOptions: StatusOptions,
          includeDueDate: true,
          nextStepOptions: Object.entries(taskStepDisplayValues).map(([value, label]) => ({
            id: value,
            value,
            label,
          })),
          priorityOptions: PriorityOptions,
          includeLastViewed: true,
          assigneeOptions: assignedToOptions ?? [],
          attestationStatusOptions: AttestationStatusOptions,
          groupOptions: (groupOptions ?? []).map(o => ({label: o.label, value: o.value, id: o.value})),
        })}
        pinnedFilters
        searchColumns={['name']}
        searchPlaceholder={currentSearchBy === TasksSearchBy.TASK_ID
          ? searchByLabels[TasksSearchBy.TASK_ID]
          : searchByLabels[TasksSearchBy.NAME]}
        searchBy={[
          {label: searchByLabels[TasksSearchBy.NAME], value: TasksSearchBy.NAME},
          {label: searchByLabels[TasksSearchBy.TASK_ID], value: TasksSearchBy.TASK_ID},
        ]}
        handleSearchByChange={handleSearchByChange}
        searchDropdownPlaceholder={currentSearchBy === TasksSearchBy.TASK_ID
          ? searchByDropdownLabels[TasksSearchBy.TASK_ID]
          : searchByDropdownLabels[TasksSearchBy.NAME]}
        tableOverrides={{
          initialFilters: {
            statuses: [StatusOptions[0]]
          },
          currentFiltersOverride: !filterMountedRef.current && !isLoadingTaskStepNames && assignedToOptions
            ? getCachedFilters() : undefined,
          initialSort: tableData[0] ? {
            tableColumn: tableData[0].eventType,
            sortOrder: SORT_ORDER.DESC
          } : null,
          count: rosterTotalRef.current,
          filteredCount: searchTotalRef.current,
          searchTerm,
          searchFunction,
          resetSortFunction: resetSort,
          sortFunction: handleSortChange,
          pinnedLabel: "Pin view to 'Your Work Summary'"
        }}
        onNextPage={handleNextPage}
        ref={tableRef}
      >
        {checkBooleanToggle(FeatureToggle.CARECO_3810_AWS_COMMUNICATIONS) && (
          <Button
            onClick={() => {
              if (mixpanelEventProperties) {
                // Attribute to the group the panel will show, resolved the same way as AwsCommunicationsDrawer.
                // With no group selected the panel shows its empty state, so the event carries no group.
                const panelRoster = activeRoster ?? selectedRoster;
                const event: MixpanelClientListEvent = {
                  eventName: MixpanelEventType.BIH_AWS_COMMUNICATIONS_OPENED,
                  ...mixpanelEventProperties,
                  ...(panelRoster ? getCurrentGroupDetails(panelRoster) : {})
                };
                carecoApi?.postMixpanelEvent(event);
              }
              // This tab is mounted at /tasks/care_journeys, so set the param in place.
              // Navigating to /tasks would hit the index redirect, which drops the query string.
              searchParams.set(SearchParam.AWS_COMMUNICATIONS, 'true');
              setSearchParams(searchParams);
            }}
          >
            <Icon
              iconClass={ICONS['address-book']}
              weight="far"
              className={css({marginRight: sizes.xSmall})}/>
                AWS Communications
          </Button>
        )}
      </SortableTable>
    </>
    <AwsCommunicationsDrawer
      isOpen={!!awsCommunications}
      onClose={closeAwsCommunicationsDrawer}/>
    </>
  );
}
